import React from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";

// Schéma de validation pour le formulaire utilisateur
const UserSchema = Yup.object().shape({
  username: Yup.string()
    .min(3, "Trop court")
    .max(50, "Trop long")
    .required("Obligatoire"),
  email: Yup.string().email("Email invalide").required("Obligatoire"),
  password: Yup.string()
    .min(6, "Le mot de passe doit contenir au moins 6 caractères")
    // Correction ici pour l'utilisation de when
    .when("isEdit", {
      is: true,
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required("Obligatoire"),
    }),
  confirmPassword: Yup.string().when("password", {
    is: (val) => val && val.length > 0,
    then: (schema) =>
      schema
        .oneOf([Yup.ref("password")], "Les mots de passe doivent correspondre")
        .required("Obligatoire"),
    otherwise: (schema) => schema.notRequired(),
  }),
  role: Yup.string()
    .oneOf(["admin", "admin_assistant", "visitor"], "Rôle invalide")
    .required("Obligatoire"),
  permissions: Yup.object().shape({
    canImport: Yup.boolean(),
    canModify: Yup.boolean(),
    canComment: Yup.boolean(),
    canExport: Yup.boolean(),
    canManageUsers: Yup.boolean(),
  }),
});

const UserForm = ({ user, onSubmit, onCancel }) => {
  const isEdit = !!user;

  const initialValues = {
    username: user?.username || "",
    email: user?.email || "",
    password: "",
    confirmPassword: "",
    role: user?.role || "visitor",
    isEdit: isEdit, // Ajout d'un champ pour la validation conditionnelle
    permissions: {
      canImport: user?.permissions?.canImport || false,
      canModify: user?.permissions?.canModify || false,
      canComment: user?.permissions?.canComment || false,
      canExport: user?.permissions?.canExport || true,
      canManageUsers: user?.permissions?.canManageUsers || false,
      canViewDashboard: user?.permissions?.canViewDashboard || true,
    },
  };

  const handleSubmit = (values, { setSubmitting }) => {
    // Supprimer la confirmation de mot de passe et le champ isEdit avant l'envoi
    const userData = { ...values };
    delete userData.confirmPassword;
    delete userData.isEdit;

    // Si le mot de passe est vide en mode édition, le supprimer
    if (isEdit && !userData.password) {
      delete userData.password;
    }

    onSubmit(userData);
    setSubmitting(false);
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={UserSchema}
      onSubmit={handleSubmit}
      validateOnBlur
      validateOnChange
    >
      {({ isSubmitting, values, setFieldValue }) => (
        <Form className="space-y-4">
          {/* Le reste du formulaire reste inchangé */}
          <div>
            <label
              htmlFor="username"
              className="block text-sm font-medium text-gray-700"
            >
              Nom d'utilisateur
            </label>
            <Field
              type="text"
              name="username"
              id="username"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
            <ErrorMessage
              name="username"
              component="div"
              className="text-red-500 text-xs mt-1"
            />
          </div>

          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700"
            >
              Email
            </label>
            <Field
              type="email"
              name="email"
              id="email"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
            <ErrorMessage
              name="email"
              component="div"
              className="text-red-500 text-xs mt-1"
            />
          </div>

          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700"
            >
              {isEdit
                ? "Nouveau mot de passe (laisser vide pour ne pas changer)"
                : "Mot de passe"}
            </label>
            <Field
              type="password"
              name="password"
              id="password"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
            <ErrorMessage
              name="password"
              component="div"
              className="text-red-500 text-xs mt-1"
            />
          </div>

          <div>
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-medium text-gray-700"
            >
              Confirmer le mot de passe
            </label>
            <Field
              type="password"
              name="confirmPassword"
              id="confirmPassword"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
            <ErrorMessage
              name="confirmPassword"
              component="div"
              className="text-red-500 text-xs mt-1"
            />
          </div>

          <div>
            <label
              htmlFor="role"
              className="block text-sm font-medium text-gray-700"
            >
              Rôle
            </label>
            <Field
              as="select"
              name="role"
              id="role"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              onChange={(e) => {
                const role = e.target.value;
                setFieldValue("role", role);

                // Mettre à jour les permissions en fonction du rôle
                switch (role) {
                  case "admin":
                    setFieldValue("permissions", {
                      canImport: true,
                      canModify: true,
                      canComment: true,
                      canExport: true,
                      canManageUsers: true,
                      canViewDashboard: true,
                    });
                    break;
                  case "admin_assistant":
                    setFieldValue("permissions", {
                      canImport: true,
                      canModify: true,
                      canComment: true,
                      canExport: true,
                      canManageUsers: false,
                    });
                    break;
                  case "visitor":
                    setFieldValue("permissions", {
                      canImport: false,
                      canModify: false,
                      canComment: false,
                      canExport: true,
                      canManageUsers: false,
                    });
                    break;
                  default:
                    setFieldValue("permissions", {
                      canImport: false,
                      canModify: false,
                      canComment: false,
                      canExport: true,
                      canManageUsers: false,
                    });
                    break;
                }
              }}
            >
              <option value="visitor">Visiteur</option>
              <option value="admin">Administrateur</option>
              <option value="admin_assistant">Assistant administrateur</option>
            </Field>
            <ErrorMessage
              name="role"
              component="div"
              className="text-red-500 text-xs mt-1"
            />
          </div>

          <div className="border rounded-md p-4 bg-gray-50">
            <h4 className="font-medium mb-2">Permissions</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <label className="flex items-center">
                  <Field
                    type="checkbox"
                    name="permissions.canImport"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Importer des données
                  </span>
                </label>
              </div>
              <div>
                <label className="flex items-center">
                  <Field
                    type="checkbox"
                    name="permissions.canModify"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Modifier les contrats
                  </span>
                </label>
              </div>
              <div>
                <label className="flex items-center">
                  <Field
                    type="checkbox"
                    name="permissions.canComment"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Ajouter des commentaires
                  </span>
                </label>
              </div>
              <div>
                <label className="flex items-center">
                  <Field
                    type="checkbox"
                    name="permissions.canExport"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Exporter les données
                  </span>
                </label>
              </div>
              <div>
                <label className="flex items-center">
                  <Field
                    type="checkbox"
                    name="permissions.canManageUsers"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    disabled={values.role !== "admin"}
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Gérer les utilisateurs
                  </span>
                </label>
              </div>

              <div>
                <label className="flex items-center">
                  <Field
                    type="checkbox"
                    name="permissions.canViewDashboard"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Accès au tableau de bord
                  </span>
                </label>
              </div>
            </div>
            <div className="mt-2 text-sm text-gray-500">
              Note: Ces permissions peuvent être modifiées individuellement pour
              ajuster finement les droits d'accès.
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4 border-t">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
            >
              {isSubmitting
                ? "Enregistrement..."
                : isEdit
                ? "Mettre à jour"
                : "Créer"}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default UserForm;
